/**
 * Chain catalog — the front-end half of the chain registry (multichain spec §4.1/§4.6).
 *
 * A chain is no longer a property of the installation but of the deal: the platform runs several
 * chains in parallel and the buyer picks one. This module is the static part of that — which
 * chains exist, and which wallet family each belongs to. Everything deployment-specific (token
 * addresses, RPC, explorer links, which chains a given caller may use) comes from api
 * `GET /buyer/chains`; nothing here is a second source of truth for those.
 *
 * Deliberately free of `import.meta.env` and of the build-time globals: `vite.config.ts` imports
 * it to turn `VITE_ENABLED_CHAINS` into the `__ENABLED_CHAINS__` / `__TRON_ENABLED__` /
 * `__EVM_ENABLED__` defines, so it must be loadable in plain Node too.
 *
 * TON is absent on purpose (spec §2): it is not EVM and takes no part in the multichain model.
 * The TON scaffold under `src/blockchain/ton/` stays in the tree, unreferenced and never bundled.
 */

/** Wallet/signature family — one family serves many chains (one EVM address covers every EVM chain). */
export type ChainFamily = 'tron' | 'evm';

/** A concrete chain the platform can settle on (api `ChainKey`). */
export type ChainKey = 'tron' | 'base' | 'ethereum';

export interface ChainCatalogEntry {
  chainKey: ChainKey;
  family: ChainFamily;
  /** Fallback label for a chain the client knows before it has read `/buyer/chains`. */
  displayName: string;
  /**
   * EVM chain ids this key can run on — mainnet and its testnet. Both are configured in wagmi
   * because which one is live is deployment state (staging settles on Base Sepolia, production
   * would settle on Base), and the front end must not hold a second opinion about it: the id of
   * a concrete deal arrives from api and the wallet is switched to it.
   */
  evmChainIds?: number[];
}

export const CHAIN_CATALOG: Record<ChainKey, ChainCatalogEntry> = {
  tron: {
    chainKey: 'tron',
    family: 'tron',
    displayName: 'TRON',
  },
  base: {
    chainKey: 'base',
    family: 'evm',
    displayName: 'Base',
    evmChainIds: [8453, 84532], // Base, Base Sepolia
  },
  ethereum: {
    chainKey: 'ethereum',
    family: 'evm',
    displayName: 'Ethereum',
    evmChainIds: [1, 11155111], // Ethereum, Sepolia
  },
};

export const ALL_CHAIN_KEYS = Object.keys(CHAIN_CATALOG) as ChainKey[];

/** What a buyer calls a wallet family — one spelling for the profile card and the listing notices. */
export const FAMILY_LABEL: Record<ChainFamily, string> = {
  tron: 'TRON',
  evm: 'Ethereum & Base (EVM)',
};

/**
 * The accessible name of an action that belongs to one family's slot: the visible label first,
 * the family after it — `Link wallet (TRON)`. Two slots put two identical buttons on one screen,
 * and the first wallet an account links becomes its primary one for good, so the name has to say
 * which family it is. It says so after the label, never inside it: speech input matches what is
 * on screen ("click Link wallet"), and WCAG 2.5.3 (Label in Name) puts the label at the start of
 * the name — words interspersed in it (`Link TRON wallet`) are a potential failure (sales#194).
 */
export const familyActionName = (visibleLabel: string, family: ChainFamily): string =>
  `${visibleLabel} (${FAMILY_LABEL[family]})`;

export const isChainKey = (value: string): value is ChainKey =>
  Object.prototype.hasOwnProperty.call(CHAIN_CATALOG, value);

/**
 * Whether api named a wallet family this storefront knows. The api's `ChainType` enum is older
 * than the multichain track and still carries `ton`, so a `chainType` read from a response is
 * checked before it picks a wallet (sales#151).
 */
export const isChainFamily = (value: unknown): value is ChainFamily =>
  value === 'tron' || value === 'evm';

export const familyOf = (chainKey: ChainKey): ChainFamily => CHAIN_CATALOG[chainKey].family;

/**
 * Parses `VITE_ENABLED_CHAINS` ("tron,base") into the chains this build carries.
 *
 * Throws on anything unknown instead of quietly dropping it: the value decides which wallet
 * stacks are bundled at all, so a typo (or `ton`, which is not part of the multichain model)
 * must fail the build rather than produce an image that silently cannot pay on the chain it
 * was meant for.
 */
export function parseEnabledChains(raw: string | undefined): ChainKey[] {
  const keys = (raw ?? '')
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

  if (keys.length === 0) {
    throw new Error('VITE_ENABLED_CHAINS is empty — list at least one chain, e.g. "tron,base"');
  }

  const unknown = keys.filter((key) => !isChainKey(key));
  if (unknown.length > 0) {
    throw new Error(
      `VITE_ENABLED_CHAINS contains unknown chains: ${unknown.join(', ')}. ` +
        `Known chains: ${ALL_CHAIN_KEYS.join(', ')}`
    );
  }

  return [...new Set(keys as ChainKey[])];
}

export const familiesOf = (chainKeys: ChainKey[]): ChainFamily[] => [
  ...new Set(chainKeys.map(familyOf)),
];

/**
 * Family used when a caller names none. TRON is the live chain and every screen that predates
 * the multichain track keeps talking to it; a build without TRON falls back to its first chain.
 */
export function resolveDefaultFamily(chainKeys: ChainKey[]): ChainFamily {
  const families = familiesOf(chainKeys);

  return families.includes('tron') ? 'tron' : families[0];
}
